/**
 * SuperAdmin — Exchanges module.
 *
 * Three subviews via a top segmented control:
 *   • New Exchange   — brand picker → outgoing + qty → replacement + expected
 *                      qty → review → confirm. Confirm POSTs /api/exchanges
 *                      which immediately reserves outgoing stock.
 *   • Exchange Status — list of pending + received, filterable, with the
 *                      "Mark as Received" action for pending rows.
 *   • Exchange Report — reuses the existing GET /api/exchanges/download
 *                      Excel endpoint (date-range picker preserved).
 *
 * Design: follows the existing clinical palette (ap-* tokens). No gradients,
 * no emoji, amber for pending, teal for received.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Repeat2, Search, ArrowRight, CheckCircle2, PackageOpen, FileSpreadsheet, Minus, Plus, AlertTriangle, XCircle,
} from 'lucide-react';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const shortDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};
const availOf = (c) => Math.max(0, Number(c.stock_quantity ?? ((c.total_stock || 0) - (c.issued_qty || 0) - (c.exchange_reserved || 0))));

const StatusPill = ({ status }) => {
  const s = (status || '').toLowerCase();
  const style = s === 'pending'
    ? { background: 'rgba(217, 119, 6, 0.12)', color: '#92400E', border: '1px solid rgba(217, 119, 6, 0.28)' }
    : s === 'received'
      ? { background: 'rgba(15, 118, 110, 0.10)', color: '#115E59', border: '1px solid rgba(15, 118, 110, 0.28)' }
      : { background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb' };
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', ...style }} data-testid={`spa-ex-status-${s}`}>
      {s || '—'}
    </span>
  );
};

function QtyStepper({ value, onChange, max, testIdPrefix }) {
  const dec = () => onChange(Math.max(1, value - 1));
  const inc = () => onChange(Math.min(max ?? 9999, value + 1));
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: 8 }}>
      <button type="button" onClick={dec} disabled={value <= 1} style={{ background: 'none', border: 'none', padding: '6px 10px', cursor: 'pointer' }} data-testid={`${testIdPrefix}-dec`}><Minus size={14} /></button>
      <input
        type="number" min={1} max={max ?? 9999} value={value}
        onChange={(e) => { const n = parseInt(e.target.value, 10); if (!Number.isNaN(n)) onChange(Math.max(1, Math.min(max ?? 9999, n))); }}
        style={{ width: 56, textAlign: 'center', border: 'none', fontSize: 15, padding: '4px 0' }}
        data-testid={`${testIdPrefix}-input`}
      />
      <button type="button" onClick={inc} disabled={max != null && value >= max} style={{ background: 'none', border: 'none', padding: '6px 10px', cursor: 'pointer' }} data-testid={`${testIdPrefix}-inc`}><Plus size={14} /></button>
    </div>
  );
}

function BrandSelector({ brands, value, onChange }) {
  return (
    <select
      className="ap-input"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid="spa-ex-brand-select"
      style={{ minWidth: 240 }}
    >
      <option value="">— Choose a brand —</option>
      {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
    </select>
  );
}

function ComponentPicker({ components, selectedId, onSelect, search, setSearch, placeholder, testIdPrefix }) {
  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return components;
    return components.filter((c) =>
      (c.name || '').toLowerCase().includes(q)
      || (c.size || '').toLowerCase().includes(q)
      || (c.description || '').toLowerCase().includes(q)
    );
  }, [components, search]);

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 10, background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
        <Search size={14} style={{ color: '#6b7280' }} />
        <input
          type="search"
          placeholder={placeholder || 'Search by name or size…'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="ap-input"
          style={{ flex: 1, border: 'none', background: 'transparent', padding: 0 }}
          data-testid={`${testIdPrefix}-search`}
        />
      </div>
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <div className="ap-empty" style={{ padding: 20 }}>No components in this brand match.</div>
        ) : filtered.map((c) => {
          const avail = availOf(c);
          const low = avail <= (c.low_stock_threshold || 0);
          const active = selectedId === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelect(c)}
              data-testid={`${testIdPrefix}-row-${c.id}`}
              style={{
                display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10, width: '100%',
                padding: '10px 14px', textAlign: 'left', borderBottom: '1px solid #f3f4f6',
                background: active ? 'rgba(15, 118, 110, 0.06)' : 'white',
                borderLeft: active ? '3px solid #0F766E' : '3px solid transparent',
                cursor: 'pointer',
              }}
            >
              <div>
                <div style={{ fontWeight: 500, color: '#111827' }}>{c.name}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{c.size || '—'} · {c.brand_name || ''}</div>
              </div>
              <div style={{ textAlign: 'right', fontSize: 13, color: '#111827' }}>
                <div><strong>{avail}</strong> avail</div>
                {Number(c.exchange_reserved || 0) > 0 && (
                  <div style={{ fontSize: 11, color: '#92400E' }}>{c.exchange_reserved} in exchange</div>
                )}
              </div>
              <div style={{ alignSelf: 'center' }}>
                {avail === 0
                  ? <span style={{ fontSize: 11, color: '#B04A3C', fontWeight: 600 }}>OUT</span>
                  : low && <span style={{ fontSize: 11, color: '#92400E', fontWeight: 600 }}>LOW</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */

export default function ExchangesPage({ ctx }) {
  const { user, showToast } = ctx;
  const [tab, setTab] = useState('new');

  // Shared data
  const [brands, setBrands] = useState([]);
  const [components, setComponents] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c, e] = await Promise.all([
        apiClient.get(endpoints.brands.list),
        apiClient.get(endpoints.components.list),
        apiClient.get(endpoints.exchanges.list),
      ]);
      setBrands(b.data || []);
      setComponents(c.data || []);
      setExchanges(e.data || []);
    } catch (err) {
      showToast && showToast(err?.response?.data?.detail || 'Failed to load exchanges', 'err');
    } finally { setLoading(false); }
  }, [showToast]);

  useEffect(() => { refresh(); }, [refresh]);

  const brandLookup = useMemo(() => Object.fromEntries(brands.map((b) => [b.id, b.name])), [brands]);

  return (
    <div data-testid="spa-exchanges-page">
      <div className="ap-eyebrow">SuperAdmin Console</div>
      <h1 className="ap-page-title">Exchanges</h1>

      {/* Segmented tabs */}
      <div style={{ display: 'inline-flex', border: '1px solid #e5e7eb', borderRadius: 10, padding: 4, background: '#f9fafb', marginBottom: 20, marginTop: 10 }}>
        {[
          { id: 'new',    label: 'New Exchange' },
          { id: 'status', label: 'Exchange Status' },
          { id: 'report', label: 'Exchange Report' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`spa-ex-tab-${t.id}`}
            style={{
              padding: '7px 14px', fontSize: 13, fontWeight: 500,
              background: tab === t.id ? 'white' : 'transparent',
              color: tab === t.id ? '#0F766E' : '#6b7280',
              border: 'none', borderRadius: 8, cursor: 'pointer',
              boxShadow: tab === t.id ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="ap-empty">Loading…</div> : (
        tab === 'new'    ? <NewExchange brands={brands} components={components} user={user} showToast={showToast} onCreated={refresh} />
      : tab === 'status' ? <ExchangeStatus exchanges={exchanges} brands={brands} brandLookup={brandLookup} showToast={showToast} onReceived={refresh} />
                          : <ExchangeReport showToast={showToast} />
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* NEW EXCHANGE                                                         */
/* ──────────────────────────────────────────────────────────────────── */

function NewExchange({ brands, components, user, showToast, onCreated }) {
  const [brandId, setBrandId] = useState('');
  const [outgoing, setOutgoing] = useState(null);
  const [replacement, setReplacement] = useState(null);
  const [outQty, setOutQty] = useState(1);
  const [expectedQty, setExpectedQty] = useState(1);
  const [reason, setReason] = useState('');

  const [outSearch, setOutSearch]   = useState('');
  const [repSearch, setRepSearch]   = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(null);

  const brandName = brands.find((b) => b.id === brandId)?.name || '';
  const brandComponents = useMemo(
    () => components.filter((c) => c.brand_id === brandId),
    [components, brandId]
  );

  // When brand changes, blow away selections.
  useEffect(() => { setOutgoing(null); setReplacement(null); setOutQty(1); setExpectedQty(1); setOutSearch(''); setRepSearch(''); }, [brandId]);
  // Keep expected qty synced to outgoing qty by default.
  useEffect(() => { setExpectedQty(outQty); }, [outQty]);

  const outAvail = outgoing ? availOf(outgoing) : 0;

  const canConfirm = brandId && outgoing && replacement && outgoing.id !== replacement.id && outQty >= 1 && outQty <= outAvail && expectedQty >= 1;

  const openConfirm = () => {
    setIdempotencyKey(`${user?.id || 'anon'}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    setConfirmOpen(true);
  };

  const submit = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const { data } = await apiClient.post(endpoints.exchanges.create, {
        item_given_id: outgoing.id,
        item_received_id: replacement.id,
        item_given_quantity: outQty,
        item_received_quantity: expectedQty,
        exchange_reason: reason || null,
        idempotency_key: idempotencyKey,
      });
      showToast(`Exchange ${data.reference} created — ${outQty} units reserved`, 'ok');
      // Reset form
      setBrandId(''); setOutgoing(null); setReplacement(null); setOutQty(1); setExpectedQty(1); setReason(''); setConfirmOpen(false);
      onCreated && onCreated();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Could not create exchange', 'err');
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="spa-ex-new">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <label className="ap-field-label" style={{ margin: 0 }}>Brand</label>
        <BrandSelector brands={brands} value={brandId} onChange={setBrandId} />
      </div>

      {!brandId ? (
        <div className="ap-empty">Select a brand to see its component inventory.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div>
            <h3 style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 10, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Sending</h3>
            <ComponentPicker
              components={brandComponents}
              selectedId={outgoing?.id}
              onSelect={(c) => { setOutgoing(c); setOutQty(1); }}
              search={outSearch}
              setSearch={setOutSearch}
              placeholder="Search outgoing component…"
              testIdPrefix="spa-ex-out"
            />
            {outgoing && (
              <div style={{ marginTop: 14, padding: 14, border: '1px solid #e5e7eb', borderRadius: 10, background: '#f9fafb' }}>
                <div style={{ fontWeight: 500 }}>{outgoing.name}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{brandName} · {outgoing.size || '—'}</div>
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>Avail: <strong style={{ color: '#111827' }}>{outAvail}</strong></span>
                  <QtyStepper value={outQty} onChange={setOutQty} max={outAvail} testIdPrefix="spa-ex-outqty" />
                </div>
                {outQty > outAvail && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#B04A3C' }}>Exceeds available stock.</div>
                )}
              </div>
            )}
          </div>

          <div>
            <h3 style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 10, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Expecting</h3>
            {!outgoing ? (
              <div className="ap-empty">Pick outgoing first.</div>
            ) : (
              <>
                <ComponentPicker
                  components={brandComponents.filter((c) => c.id !== outgoing.id)}
                  selectedId={replacement?.id}
                  onSelect={(c) => setReplacement(c)}
                  search={repSearch}
                  setSearch={setRepSearch}
                  placeholder="Search replacement component…"
                  testIdPrefix="spa-ex-rep"
                />
                {replacement && (
                  <div style={{ marginTop: 14, padding: 14, border: '1px solid #e5e7eb', borderRadius: 10, background: '#f9fafb' }}>
                    <div style={{ fontWeight: 500 }}>{replacement.name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{brandName} · {replacement.size || '—'} · {availOf(replacement)} avail</div>
                    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>Expected qty</span>
                      <QtyStepper value={expectedQty} onChange={setExpectedQty} testIdPrefix="spa-ex-expqty" />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {outgoing && replacement && (
        <>
          <div style={{ marginTop: 24 }}>
            <label className="ap-field-label">Reason (optional)</label>
            <input
              type="text"
              className="ap-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. patient needed Ø4.5×6mm instead of ×5mm"
              data-testid="spa-ex-reason"
            />
          </div>
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="ap-btn ap-btn-primary" onClick={openConfirm} disabled={!canConfirm} data-testid="spa-ex-review-btn">
              Review & Confirm <ArrowRight size={14} style={{ marginLeft: 6 }} />
            </button>
          </div>
        </>
      )}

      {confirmOpen && (
        <ConfirmExchangeModal
          outgoing={outgoing} replacement={replacement}
          brandName={brandName} outQty={outQty} expectedQty={expectedQty} reason={reason}
          submitting={submitting} user={user}
          onCancel={() => !submitting && setConfirmOpen(false)}
          onConfirm={submit}
        />
      )}
    </div>
  );
}

function ConfirmExchangeModal({ outgoing, replacement, brandName, outQty, expectedQty, reason, submitting, user, onCancel, onConfirm }) {
  return (
    <div
      role="dialog" aria-modal="true"
      style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      data-testid="spa-ex-confirm-modal"
    >
      <div style={{ background: 'white', borderRadius: 10, width: '100%', maxWidth: 500, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#6b7280' }}>Review exchange</div>
          <div style={{ fontWeight: 600, fontSize: 16, marginTop: 2 }}>Confirm before stock is reserved</div>
        </div>
        <div style={{ padding: '18px 22px' }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#92400E', marginBottom: 4 }}>Sending</div>
            <div style={{ fontWeight: 500 }}>{outgoing.name} — {brandName} — {outgoing.size || '—'}</div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>Quantity: <strong>{outQty}</strong></div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#115E59', marginBottom: 4 }}>Expecting</div>
            <div style={{ fontWeight: 500 }}>{replacement.name} — {brandName} — {replacement.size || '—'}</div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>Expected quantity: <strong>{expectedQty}</strong></div>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
            Initiated by <strong style={{ color: '#111827' }}>{user?.full_name || user?.username}</strong> · {new Date().toLocaleString()}
          </div>
          {reason && <div style={{ fontSize: 13, color: '#374151', background: '#f9fafb', padding: '8px 10px', borderRadius: 6 }}>{reason}</div>}
        </div>
        <div style={{ padding: '14px 22px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="ap-btn ap-btn-ghost" onClick={onCancel} disabled={submitting} data-testid="spa-ex-confirm-cancel">Cancel</button>
          <button type="button" className="ap-btn ap-btn-primary" onClick={onConfirm} disabled={submitting} data-testid="spa-ex-confirm-submit">
            {submitting ? 'Confirming…' : 'Confirm Exchange'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* EXCHANGE STATUS                                                      */
/* ──────────────────────────────────────────────────────────────────── */

function ExchangeStatus({ exchanges, brands, brandLookup, showToast, onReceived }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [brandFilter, setBrandFilter]   = useState('');
  const [search, setSearch]             = useState('');
  const [receiveTarget, setReceiveTarget] = useState(null);
  const [cancelTarget, setCancelTarget]   = useState(null);
  const [cancelling, setCancelling]       = useState(false);

  const doCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await apiClient.put(endpoints.exchanges.cancel(cancelTarget.id));
      showToast(`Exchange ${cancelTarget.reference || cancelTarget.id.slice(0, 8)} cancelled — units released`, 'ok');
      setCancelTarget(null);
      onReceived && onReceived();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Could not cancel', 'err');
    } finally { setCancelling(false); }
  };

  const brandIdOfExchange = (ex) => {
    // We don't persist brand on the exchange — resolve via item_given_name match if needed.
    // Simplification: just search by name.
    return null;
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (exchanges || []).filter((ex) => {
      if (statusFilter !== 'all' && (ex.status || '') !== statusFilter) return false;
      if (q && !(
        (ex.item_given_name || '').toLowerCase().includes(q)
        || (ex.item_received_name || '').toLowerCase().includes(q)
        || (ex.reference || '').toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [exchanges, statusFilter, search]);

  const counts = useMemo(() => {
    let p = 0, r = 0, c = 0, x = 0;
    for (const ex of exchanges || []) {
      if (ex.status === 'pending') p++;
      else if (ex.status === 'received') r++;
      else if (ex.status === 'cancelled') x++;
      else c++;
    }
    return { pending: p, received: r, completed: c, cancelled: x };
  }, [exchanges]);

  return (
    <div data-testid="spa-ex-status">
      <div className="ap-ind-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
        <div className="ap-ind" data-testid="spa-ex-stat-pending">
          <div className="ap-ind-label">Pending</div>
          <div className="ap-ind-value" style={{ color: counts.pending > 0 ? '#92400E' : undefined }}>{counts.pending}</div>
        </div>
        <div className="ap-ind" data-testid="spa-ex-stat-received">
          <div className="ap-ind-label">Received</div>
          <div className="ap-ind-value" style={{ color: counts.received > 0 ? '#115E59' : undefined }}>{counts.received}</div>
        </div>
        <div className="ap-ind" data-testid="spa-ex-stat-cancelled">
          <div className="ap-ind-label">Cancelled</div>
          <div className="ap-ind-value" style={{ color: counts.cancelled > 0 ? '#6b7280' : undefined }}>{counts.cancelled}</div>
        </div>
        <div className="ap-ind" data-testid="spa-ex-stat-legacy">
          <div className="ap-ind-label">Legacy</div>
          <div className="ap-ind-value">{counts.completed}</div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <select className="ap-input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="spa-ex-filter-status">
          <option value="all">All statuses</option>
          <option value="pending">Pending</option>
          <option value="received">Received</option>
          <option value="cancelled">Cancelled</option>
          <option value="completed">Legacy</option>
        </select>
        <input
          type="search"
          className="ap-input"
          placeholder="Search by component or reference…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 240 }}
          data-testid="spa-ex-filter-search"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="ap-empty" data-testid="spa-ex-empty">No exchanges match these filters.</div>
      ) : (
        <div className="ap-table-wrap">
          <table className="ap-table" data-testid="spa-ex-table">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Status</th>
                <th>Sent</th>
                <th>Expected</th>
                <th>Received</th>
                <th>Created</th>
                <th>Receipt</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((ex) => (
                <tr key={ex.id} data-testid={`spa-ex-row-${ex.id}`}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{ex.reference || ex.id.slice(0, 8).toUpperCase()}</div>
                    <div className="ap-subtle" style={{ fontSize: 11 }}>{ex.admin_name || '—'}</div>
                  </td>
                  <td><StatusPill status={ex.status} /></td>
                  <td>
                    <div>{ex.item_given_name}</div>
                    <div className="ap-subtle" style={{ fontSize: 11 }}>Qty {ex.item_given_quantity}</div>
                  </td>
                  <td>
                    <div>{ex.item_received_name}</div>
                    <div className="ap-subtle" style={{ fontSize: 11 }}>Qty {ex.expected_quantity ?? ex.item_received_quantity}</div>
                  </td>
                  <td>
                    {ex.received_quantity != null ? (
                      <>
                        <strong>{ex.received_quantity}</strong>
                        {ex.discrepancy ? (
                          <span style={{ marginLeft: 6, fontSize: 11, color: '#B04A3C' }}>
                            ({ex.discrepancy > 0 ? '+' : ''}{ex.discrepancy})
                          </span>
                        ) : null}
                      </>
                    ) : '—'}
                  </td>
                  <td>{shortDate(ex.created_at)}</td>
                  <td>{shortDate(ex.received_at)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {ex.status === 'pending' ? (
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        <button
                          type="button"
                          className="ap-btn ap-btn-sm ap-btn-primary"
                          onClick={() => setReceiveTarget(ex)}
                          data-testid={`spa-ex-receive-${ex.id}`}
                        >
                          <PackageOpen size={13} /> Receive
                        </button>
                        <button
                          type="button"
                          className="ap-btn ap-btn-sm ap-btn-ghost"
                          onClick={() => setCancelTarget(ex)}
                          data-testid={`spa-ex-cancel-${ex.id}`}
                          style={{ color: '#B04A3C', borderColor: 'rgba(176, 74, 60, 0.3)' }}
                        >
                          <XCircle size={13} /> Cancel
                        </button>
                      </div>
                    ) : ex.status === 'received' ? (
                      <span style={{ fontSize: 12, color: '#115E59', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <CheckCircle2 size={13} /> Credited
                      </span>
                    ) : ex.status === 'cancelled' ? (
                      <span style={{ fontSize: 12, color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <XCircle size={13} /> Released
                      </span>
                    ) : <span className="ap-subtle" style={{ fontSize: 12 }}>legacy</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {receiveTarget && (
        <ReceiveModal
          exchange={receiveTarget}
          showToast={showToast}
          onClose={() => setReceiveTarget(null)}
          onDone={() => { setReceiveTarget(null); onReceived && onReceived(); }}
        />
      )}

      {cancelTarget && (
        <div role="dialog" aria-modal="true"
          style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
          data-testid="spa-ex-cancel-modal"
        >
          <div style={{ background: 'white', borderRadius: 10, width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#6b7280' }}>Cancel exchange</div>
              <div style={{ fontWeight: 600, fontSize: 16, marginTop: 2 }}>{cancelTarget.reference || cancelTarget.id.slice(0, 8)}</div>
            </div>
            <div style={{ padding: '18px 22px' }}>
              <div style={{ fontSize: 14, marginBottom: 10 }}>
                Release <strong>{cancelTarget.item_given_quantity}×</strong> {cancelTarget.item_given_name} back to available stock?
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Students and Clerks will immediately see these units again.</div>
            </div>
            <div style={{ padding: '14px 22px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="ap-btn ap-btn-ghost" onClick={() => setCancelTarget(null)} disabled={cancelling} data-testid="spa-ex-cancel-cancel">Keep</button>
              <button type="button" className="ap-btn ap-btn-danger" onClick={doCancel} disabled={cancelling} data-testid="spa-ex-cancel-confirm">
                {cancelling ? 'Cancelling…' : 'Cancel exchange'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReceiveModal({ exchange, showToast, onClose, onDone }) {
  const expected = Number(exchange.expected_quantity ?? exchange.item_received_quantity ?? 0);
  const [qty, setQty] = useState(expected);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const discrepancy = qty - expected;

  const submit = async () => {
    setSubmitting(true);
    try {
      await apiClient.put(endpoints.exchanges.receive(exchange.id), { received_quantity: Number(qty), note: note || null });
      showToast(`Exchange ${exchange.reference || exchange.id.slice(0, 8)} marked Received`, 'ok');
      onDone();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Could not mark as received', 'err');
    } finally { setSubmitting(false); }
  };

  return (
    <div role="dialog" aria-modal="true"
      style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      data-testid="spa-ex-receive-modal"
    >
      <div style={{ background: 'white', borderRadius: 10, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#6b7280' }}>Confirm receipt</div>
          <div style={{ fontWeight: 600, fontSize: 16, marginTop: 2 }}>{exchange.reference || exchange.id.slice(0, 8)}</div>
        </div>
        <div style={{ padding: '18px 22px' }}>
          <div style={{ fontSize: 13, marginBottom: 12 }}>
            <div style={{ color: '#92400E', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 2 }}>Received</div>
            <div style={{ fontWeight: 500 }}>{exchange.item_received_name}</div>
            <div className="ap-subtle" style={{ fontSize: 12 }}>Expected qty: {expected}</div>
          </div>
          <label className="ap-field-label">Actual quantity received</label>
          <QtyStepper value={qty} onChange={setQty} testIdPrefix="spa-ex-receive-qty" />
          {discrepancy !== 0 && (
            <div style={{ marginTop: 10, padding: '8px 10px', background: 'rgba(176, 74, 60, 0.08)', border: '1px solid rgba(176, 74, 60, 0.25)', borderRadius: 8, color: '#8B2E1F', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={14} />
              <span>
                {discrepancy > 0 ? `Surplus of ${discrepancy} unit${discrepancy === 1 ? '' : 's'}` : `Short by ${Math.abs(discrepancy)} unit${Math.abs(discrepancy) === 1 ? '' : 's'}`}
                {' '}— only the actual quantity will be credited to inventory.
              </span>
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <label className="ap-field-label">Note (optional)</label>
            <input type="text" className="ap-input" value={note} onChange={(e) => setNote(e.target.value)} data-testid="spa-ex-receive-note" placeholder="e.g. supplier shipped partial batch" />
          </div>
        </div>
        <div style={{ padding: '14px 22px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="ap-btn ap-btn-ghost" onClick={onClose} disabled={submitting} data-testid="spa-ex-receive-cancel">Cancel</button>
          <button type="button" className="ap-btn ap-btn-primary" onClick={submit} disabled={submitting} data-testid="spa-ex-receive-submit">
            {submitting ? 'Crediting…' : 'Confirm Receipt'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* EXCHANGE REPORT (reuses existing /exchanges/download Excel)          */
/* ──────────────────────────────────────────────────────────────────── */

function ExchangeReport({ showToast }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const url = `${endpoints.exchanges.download}?from_date=${from}&to_date=${to}`;
      const res = await apiClient.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = `Exchange_Report_${from}_to_${to}.xlsx`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(a.href);
      showToast && showToast('Report downloaded', 'ok');
    } catch (e) {
      showToast && showToast('Could not download report', 'err');
    } finally { setDownloading(false); }
  };

  return (
    <div data-testid="spa-ex-report">
      <div style={{ display: 'flex', alignItems: 'end', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <label className="ap-field-label">From</label>
          <input type="date" className="ap-input" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="spa-ex-report-from" />
        </div>
        <div>
          <label className="ap-field-label">To</label>
          <input type="date" className="ap-input" value={to} onChange={(e) => setTo(e.target.value)} data-testid="spa-ex-report-to" />
        </div>
        <button type="button" className="ap-btn ap-btn-primary" onClick={download} disabled={downloading || !from || !to} data-testid="spa-ex-report-download">
          <FileSpreadsheet size={14} /> {downloading ? 'Preparing…' : 'Download Excel'}
        </button>
      </div>
    </div>
  );
}
